/**
 * Top-bar toggle button for the Reviews panel.
 * Shows a badge with the count of "open" reviewer comments (pending + accepted).
 * Lives in its own component so the heavy useReviews hook only mounts when this button renders.
 */

import { MessageSquare } from 'lucide-react';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { useReviews } from './useReviews';
import { IconTipB } from '@/components/studio/IconTipB';

interface Props {
  conversationId: string;
  onClick: () => void;
  /** `icon`: Direction B's top bar, an icon with a dot for new comments, pressed while the panel is open. */
  variant?: 'label' | 'icon';
  active?: boolean;
}

export function ReviewsToggleButton({ conversationId, onClick, variant = 'label', active = false }: Props) {
  const { reviews } = useReviews(conversationId, true);

  // Only flag truly-untriaged comments. Once accepted (queued) they live in the
  // chat chip stack — surfacing them again on the toggle would feel like dupes.
  const openCount = reviews.reduce(
    (acc, r) => acc + r.comments.filter((c) => c.status === 'pending').length,
    0,
  );

  if (variant === 'icon') {
    const label = openCount > 0 ? `Reviews, ${openCount} new` : 'Reviews';
    return (
      <IconTipB label={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        onClick={onClick}
        className={`relative grid h-8 w-8 place-items-center rounded-[var(--b-radius-control)] transition-colors ${active ? 'bg-b-raised text-b-t1' : 'text-b-t3 hover:bg-b-raised hover:text-b-t1'}`}
      >
        <MessageSquare className="h-[18px] w-[18px]" />
        {openCount > 0 && <span aria-hidden="true" className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-warning" />}
      </button>
      </IconTipB>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClick}
          className="h-8 px-2.5 gap-1.5 studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 relative"
        >
          <MessageSquare className="w-4 h-4" />
          <span className="text-xs">Reviews</span>
          {openCount > 0 && (
            <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-warning text-warning-foreground text-[10px] font-semibold tabular-nums">
              {openCount}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        Open Reviews Panel
      </TooltipContent>
    </Tooltip>
  );
}
